// Queries
import LIQUIDATION from '@/assets/graphql/queries/liquidations/Liquidation.graphql';
import LIQUIDATIONS from '@/assets/graphql/queries/liquidations/Liquidations.graphql';
import PARTNER_REFUND from '@/assets/graphql/queries/liquidations/PartnerRefund.graphql';
import PARTNER_REFUNDS from '@/assets/graphql/queries/liquidations/PartnerRefunds.graphql';
import PREPAID_VALIDATIONS from '@/assets/graphql/queries/liquidations/PrepaidValidations.graphql';
import PREPAID_VALIDATIONS_TRAVEL from '@/assets/graphql/queries/liquidations/PrepaidValidationsTravel.graphql';
import RESERVATION_VALIDATIONS from '@/assets/graphql/queries/liquidations/ReservationValidations.graphql';
import RESERVATION_VALIDATIONS_BY_CURRENCY from '@/assets/graphql/queries/liquidations/ReservationValidationsByCurrency.graphql';
import VALIDATION_CANCELLATIONS from '@/assets/graphql/queries/liquidations/ValidationCancellations.graphql';
import TRAVEL_RESERVATION_VALIDATIONS from '@/assets/graphql/queries/liquidations/TravelReservationValidations.graphql';
import PARTNERS_LIQUIDATION_PREFERENCES from '@/assets/graphql/queries/liquidations/PartnersLiquidationPreferences.graphql';
import BILL_TYPES from '@/assets/graphql/queries/liquidations/BillTypes.graphql';
//  Mutations
import CREATE_BILL_FILE_MUTATION from '@/assets/graphql/mutations/liquidations/CreateBillFileMutation.graphql';
import CREATE_LIQUIDATION_MUTATION from '@/assets/graphql/mutations/liquidations/CreateLiquidationMutation.graphql';
import DELETE_BILL_FILE_MUTATION from '@/assets/graphql/mutations/liquidations/DeleteBillFileMutation.graphql';
import DELETE_LIQUIDATION from '@/assets/graphql/mutations/liquidations/DeleteLiquidation.graphql';
import PROCESS_XML_MEX_DOCUMENT_MUTATION from '@/assets/graphql/mutations/liquidations/ProcessXMLMexDocumentMutation.graphql';
import XML_LIQUIDATION_MUTATION from '@/assets/graphql/mutations/liquidations/XMLLiquidationMutation.graphql';
import XML_AND_PDF_LIQUIDATION_MUTATION from '@/assets/graphql/mutations/liquidations/XMLAndPDFLiquidationMutation.graphql';
import CREATE_LIQUIDATION_EXPRESS_MUTATION from '@/assets/graphql/mutations/liquidations/CreateLiquidationExpressMutation.graphql';
import CREATE_REFUND_MUTATION from '@/assets/graphql/mutations/liquidations/CreateRefundMutation.graphql';
import CREATE_REFUND_ARG_MUTATION from '@/assets/graphql/mutations/liquidations/CreateRefundARGMutation.graphql';
import VERIFY_BILL_NUMBER_MUTATION from '@/assets/graphql/mutations/liquidations/VerifyBillNumberMutation.graphql';

// service
import apolloService from '@/services/apolloService';
import { axiosApi } from '@/config/http';

import * as Types from './types';
import type {
  URYDocumentBillData,
  ARGDocumentBillData,
  PERDocumentBillData,
  BillData,
  NewLiquidationPERPayload,
} from '@/models/LiquidationInterface';
import type { ApiResponse, ErrorResponse } from '@/config/http/types';
import type { BillTypeItem } from './types';

export default class {
  // QUERIES
  static getLiquidation(variables: { id: string }) {
    return apolloService.query<Types.PartnerLiquidationResponse>(
      LIQUIDATION,
      variables,
      {
        fetchPolicy: 'no-cache',
      },
    );
  }

  static getLiquidations(variables: { code: string }) {
    return apolloService.query<Types.PartnerLiquidationsResponse>(
      LIQUIDATIONS,
      variables,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getRefunds() {
    return apolloService.query<Types.PartnerRefundsResponse>(
      PARTNER_REFUNDS,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getRefund(variables: { id: string }) {
    return apolloService.query<Types.PartnerRefundResponse>(
      PARTNER_REFUND,
      variables,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getPrepaidValidations() {
    return apolloService.query<Types.PrepaidValidationsResponse>(
      PREPAID_VALIDATIONS,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getPrepaidTravelValidations() {
    return apolloService.query<Types.PrepaidTravelValidationsResponse>(
      PREPAID_VALIDATIONS_TRAVEL,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getValidationCancellations() {
    return apolloService.query<Types.ValidationCancellationsResponse>(
      VALIDATION_CANCELLATIONS,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }
  static getReservationValidations() {
    return apolloService.query<Types.ReservationValidationsResponse>(
      RESERVATION_VALIDATIONS,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getReservationValidationsByCurrency() {
    return apolloService.query<Types.ReservationValidationsByCurrencyResponse>(
      RESERVATION_VALIDATIONS_BY_CURRENCY,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getTravelReservationValidations() {
    return apolloService.query<Types.TravelReservationValidationsResponse>(
      TRAVEL_RESERVATION_VALIDATIONS,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getsLiquidationPreferences() {
    return apolloService.query<Types.PartnerLiquidationPreferencesResponse>(
      PARTNERS_LIQUIDATION_PREFERENCES,
      undefined,
      {
        fetchPolicy: 'network-only',
      },
    );
  }

  static getBillTypes() {
    return apolloService.query<Types.BillTypesResponse>(BILL_TYPES, undefined, {
      fetchPolicy: 'network-only',
    });
  }

  static getBillTypesByCountry(
    country: string,
    params?: Record<string, string>,
  ) {
    return axiosApi.get<ApiResponse<BillTypeItem[]>>(
      `/v1/liquidation/${country}/bill-types`,
      { params },
    );
  }

  // MUTATIONS
  static createBillFile(variables: Types.CreateBillFilePayload) {
    return apolloService.mutate<Types.CreateBillFileResposne>(
      CREATE_BILL_FILE_MUTATION,
      variables,
      {},
      'upload',
    );
  }

  static createLiquidation(variables: Types.NewLiquidationPayload) {
    return apolloService.mutate<Types.CreateLiquidationResponse>(
      CREATE_LIQUIDATION_MUTATION,
      variables,
      {},
      'upload',
    );
  }

  static deleteLiquidationBillFile(variables: { document_id: string }) {
    return apolloService.mutate<Types.DeleteBillFileResponse>(
      DELETE_BILL_FILE_MUTATION,
      variables,
    );
  }

  static deleteLiquidation(variables: { liquidation_id: string }) {
    return apolloService.mutate<Types.DeleteLiquidationResponse>(
      DELETE_LIQUIDATION,
      variables,
    );
  }

  static async processXMLUryDocument(variables: { file: File }) {
    let ok = true;
    let response: BillData[] = [];
    let error = '';
    try {
      const formData = new FormData();
      formData.append('file', variables.file);
      const axiosResponse = await axiosApi.post<ApiResponse<BillData[]>>(
        '/v1/dgi/parse-xml-form/',
        formData,
      );
      response = axiosResponse.data.response;
    } catch (e) {
      ok = false;
      const axiosError = e as ErrorResponse;
      const errTitle = axiosError.response.data.message;
      const errDetail = axiosError.response.data.response
        .map((f: string) => `<li>- ${f}</li>`)
        .join('');
      error = `<p>${errTitle}:</p> <p><ul>${errDetail}</ul></p>`;
    }
    return { ok, response, error };
  }
  static createXMLLiquidation(variables: Types.CreateXMLLiquidationPayload) {
    return apolloService.mutate<Types.CreateXMLLiquidationResponse>(
      XML_LIQUIDATION_MUTATION,
      variables,
      {},
      'upload',
    );
  }

  static async processArgPDFDocument(variables: { billDocument: File }) {
    let ok = true;
    let response: ARGDocumentBillData | null = null;
    let error = '';
    try {
      const formData = new FormData();
      formData.append('file', variables.billDocument);
      const axiosResponse = await axiosApi.post<
        ApiResponse<ARGDocumentBillData>
      >('/v1/liquidation/ARG/process-pdf-invoice', formData);
      response = axiosResponse.data.response;
    } catch (e) {
      ok = false;
      const axiosError = e as ErrorResponse;
      const errTitle = axiosError.response.data.message;
      const errDetail = axiosError.response.data.response
        .map((f: string) => `<li>- ${f}</li>`)
        .join('');
      error = `<p>${errTitle}:</p> <p><ul>${errDetail}</ul></p>`;
    }
    return { ok, response, error };
  }

  static async processUryPDFDocument(variables: { billDocument: File }) {
    let ok = true;
    let response: URYDocumentBillData | null = null;
    let error = '';
    try {
      const formData = new FormData();
      formData.append('file', variables.billDocument);
      const axiosResponse = await axiosApi.post<
        ApiResponse<URYDocumentBillData>
      >('/v1/liquidation/URY/process-pdf-invoice', formData);
      response = axiosResponse.data.response;
    } catch (e) {
      ok = false;
      const axiosError = e as ErrorResponse;
      const errTitle = axiosError.response.data.message;
      const errDetail = axiosError.response.data.response
        .map((f: string) => `<li>- ${f}</li>`)
        .join('');
      error = `<p>${errTitle}:</p> <p><ul>${errDetail}</ul></p>`;
    }
    return { ok, response, error };
  }
  static async processPERPDFDocument(variables: { billDocument: File }) {
    let ok = true;
    let response: PERDocumentBillData | null = null;
    let error = '';
    try {
      const formData = new FormData();
      formData.append('file', variables.billDocument);
      const axiosResponse = await axiosApi.post<
        ApiResponse<PERDocumentBillData>
      >('/v1/liquidation/PER/process-pdf-invoice', formData);
      response = axiosResponse.data.response;
    } catch (e) {
      ok = false;
      const axiosError = e as ErrorResponse;
      const errTitle = axiosError.response.data.message;
      const errDetail = axiosError.response.data.response
        .map((f: string) => `<li>- ${f}</li>`)
        .join('');
      error = `<p>${errTitle}:</p> <p><ul>${errDetail}</ul></p>`;
    }
    return { ok, response, error };
  }

  static processXMLMexDocument(variables: { billDocument: File }) {
    return apolloService.mutate<Types.ProcessXMLMexDocumentResponse>(
      PROCESS_XML_MEX_DOCUMENT_MUTATION,
      variables,
      {},
      'upload',
    );
  }
  static createXMLAndPDFLiquidation(variables: {
    data: Types.CreateXMLAndPDFLiquidationPayload;
  }) {
    return apolloService.mutate<Types.CreateXMLAndPDFLiquidationResponse>(
      XML_AND_PDF_LIQUIDATION_MUTATION,
      variables,
      {},
      'upload',
    );
  }

  static createLiquidationExpress(
    variables: Types.NewExpressLiquidationPayload,
  ) {
    return apolloService.mutate<Types.CreateLiquidationExpressResponse>(
      CREATE_LIQUIDATION_EXPRESS_MUTATION,
      variables,
      {},
      'upload',
    );
  }
  static verifyBillNumber(variables: Types.VerifyBillPayload) {
    return apolloService.mutate<Types.VerifyBillResponse>(
      VERIFY_BILL_NUMBER_MUTATION,
      variables,
    );
  }

  static createRefund(variables: Types.NewRefundPayload) {
    return apolloService.mutate<Types.CreateRefundResponse>(
      CREATE_REFUND_MUTATION,
      variables,
      {},
      'upload',
    );
  }

  static async createLiquidationPER(payload: NewLiquidationPERPayload) {
    let ok = true;
    let response: Types.LiquidationPERResult | null = null;
    let error = '';
    try {
      const formData = new FormData();
      if (payload.billDocument) {
        formData.append('bill_document', payload.billDocument);
      }
      formData.append(
        'data',
        JSON.stringify({
          liquidable_codes: payload.liquidable_codes,
          bill_type: payload.type,
          bill_number: payload.number,
          bill_date: payload.date,
          bill_amount: payload.amount,
          total_amount: payload.totalAmount,
          series: payload.series,
          due_date: payload.dueDate,
          emitter_ruc: payload.emitterRuc,
          receiver_ruc: payload.bigboxRuc || null,
          modified: payload.modified,
          tax_rate: payload.taxRate,
          service_charge_rate: payload.serviceChargeRate,
        }),
      );
      const axiosResponse = await axiosApi.post<
        ApiResponse<Types.LiquidationPERResult>
      >('/v1/liquidation/PER/create-liquidation', formData);
      response = axiosResponse.data.response;
    } catch (e) {
      ok = false;
      const axiosError = e as ErrorResponse;
      const errData = axiosError.response.data;
      if (String(errData.internal_code).startsWith('4')) {
        const summary = errData.response?.summary;
        const detailMessages = Array.isArray(summary)
          ? summary
              .map((item: { message?: string }) => item.message)
              .filter(Boolean)
          : [];
        error = detailMessages.length
          ? `${errData.message}: ${detailMessages.join(', ')}`
          : errData.message;
      } else {
        const errDetail = errData.response
          .map((f: string) => `<li>- ${f}</li>`)
          .join('');
        error = `<p>${errData.message}:</p> <p><ul>${errDetail}</ul></p>`;
      }
    }
    return { ok, response, error };
  }

  static createRefundARG(variables: Types.NewRefundARGPayload) {
    return apolloService.mutate<Types.CreateRefundARGResponse>(
      CREATE_REFUND_ARG_MUTATION,
      variables,
      {},
      'upload',
    );
  }
}
